import { memo, useCallback, useEffect, useMemo, useState } from "react";
import "./CollapsibleProgressiveActionsTable.scss";

export type CollapsibleProgressiveActionsRow = {
  category: string;
  section: string;
  actionName: string;
  owner: string;
  completedBy: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

interface CollapsibleProgressiveActionsTableProps {
  rows: CollapsibleProgressiveActionsRow[];
  darkMode: boolean;
  resetToken?: string;
}

const DEFAULT_SECTION_LABEL = "General";

const CollapsibleProgressiveActionsTable = memo((props: CollapsibleProgressiveActionsTableProps) => {
  const categories = useMemo(() => {
    const categoryMap = new Map<
      string,
      {
        category: string;
        sections: Map<string, CollapsibleProgressiveActionsRow[]>;
      }
    >();

    props.rows.forEach((row) => {
      const rawCategory = typeof row.category === "string" ? row.category.trim() : "";
      const rawSection = typeof row.section === "string" ? row.section.trim() : "";

      const category = rawCategory || "";
      const section = rawSection || DEFAULT_SECTION_LABEL;

      const existingCategory = categoryMap.get(category);
      if (!existingCategory) {
        categoryMap.set(category, {
          category,
          sections: new Map<string, CollapsibleProgressiveActionsRow[]>(),
        });
      }

      const catEntry = categoryMap.get(category)!;
      const existingSectionRows = catEntry.sections.get(section) ?? [];
      catEntry.sections.set(section, [...existingSectionRows, row]);
    });

    return Array.from(categoryMap.values()).map((c) => ({
      category: c.category,
      sections: Array.from(c.sections.entries()).map(([section, rows]) => ({ section, rows })),
    }));
  }, [props.rows]);

  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  // Only reset collapsed state when the selected audit changes.
  // Row updates (e.g., ticking a checkbox) should preserve current expansion state.
  useEffect(() => {
    const nextCollapsedCategories: Record<string, boolean> = {};
    const nextCollapsedSections: Record<string, boolean> = {};

    categories.forEach((cat) => {
      nextCollapsedCategories[cat.category] = true;
      cat.sections.forEach((sec) => {
        nextCollapsedSections[`${cat.category}||${sec.section}`] = true;
      });
    });

    setCollapsedCategories(nextCollapsedCategories);
    setCollapsedSections(nextCollapsedSections);
  }, [props.resetToken]);

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((current) => ({
      ...current,
      [category]: !current[category],
    }));
  }, []);

  const toggleSection = useCallback((category: string, section: string) => {
    const key = `${category}||${section}`;
    setCollapsedSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }, []);

  if (!props.rows || props.rows.length === 0) return null;

  return (
    <div className={"sc-collapsible-progressive-actions" + (props.darkMode ? " dark" : "")}
    >
      <div className="sc-cpa-header">
        <div>Action</div>
        <div>Owner</div>
        <div>Completed By</div>
      </div>

      <div className="sc-cpa-body">
        {categories.map((cat, catIdx) => {
          const isCategoryCollapsed = Boolean(collapsedCategories[cat.category]);
          const categoryLabel = cat.category || "";
          const categoryCaret = isCategoryCollapsed ? "▸" : "▾";

          return (
            <div key={`cat-${catIdx}-${cat.category}`}>
              <button
                type="button"
                className="sc-cpa-toggle sc-cpa-category"
                onClick={() => toggleCategory(cat.category)}
                aria-expanded={!isCategoryCollapsed}
              >
                <span className="sc-cpa-caret">{categoryCaret}</span>
                <span className="sc-cpa-label">{categoryLabel}</span>
              </button>

              {!isCategoryCollapsed && (
                <div>
                  {cat.sections.map((sec, secIdx) => {
                    const sectionKey = `${cat.category}||${sec.section}`;
                    const isSectionCollapsed = Boolean(collapsedSections[sectionKey]);
                    const sectionCaret = isSectionCollapsed ? "▸" : "▾";

                    return (
                      <div key={`sec-${catIdx}-${secIdx}-${sectionKey}`}>
                        <button
                          type="button"
                          className="sc-cpa-toggle sc-cpa-section"
                          onClick={() => toggleSection(cat.category, sec.section)}
                          aria-expanded={!isSectionCollapsed}
                        >
                          <span className="sc-cpa-caret">{sectionCaret}</span>
                          <span className="sc-cpa-label">{sec.section}</span>
                        </button>

                        {!isSectionCollapsed && (
                          <div>
                            {sec.rows.map((row, idx) => {
                              const checkboxId = `cpa-${catIdx}-${secIdx}-${idx}`;
                              return (
                                <div key={`row-${sectionKey}-${idx}`} className="sc-cpa-row">
                                  <div className="sc-cpa-cell sc-cpa-action">
                                    <div className="sc-cpa-action-inner">
                                      <input
                                        id={checkboxId}
                                        type="checkbox"
                                        checked={Boolean(row.checked)}
                                        onChange={(e) => row.onToggle(e.target.checked)}
                                      />
                                      <label htmlFor={checkboxId}>{row.actionName}</label>
                                    </div>
                                  </div>
                                  <div className="sc-cpa-cell">{row.owner}</div>
                                  <div className="sc-cpa-cell">{row.completedBy}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="sc-cpa-divider" />
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CollapsibleProgressiveActionsTable;
